import { db } from './db.js';
import { baseRecord } from './id.js';
export class RecordConflictError extends Error {
    current;
    constructor(current) {
        super('다른 곳에서 기록이 바뀌었어요. 입력은 남겨두었으니 현재 기록을 확인해 주세요.');
        this.current = current;
        this.name = 'RecordConflictError';
    }
}
export class LocalRepository {
    actorLabelResolvers = new Set();
    /** Channel UI supplies the active household actor. Storage stays framework-independent. */
    registerActorLabelResolver(resolver) {
        this.actorLabelResolvers.add(resolver);
        return () => { this.actorLabelResolvers.delete(resolver); };
    }
    async list(table, includeDeleted = false) {
        const rows = await db[table].toArray();
        return (includeDeleted ? rows : rows.filter((row) => !row.deletedAt && !row.duplicateOf)).sort((a, b) => b.updatedAt - a.updatedAt);
    }
    async get(table, id) {
        return db[table].get(id);
    }
    async put(table, value) {
        const typed = value;
        const target = db[table];
        return db.transaction('rw', target, async () => {
            let existing = typed.id ? await target.get(typed.id) : undefined;
            if (existing && typed.updatedAt != null && typed.updatedAt !== existing.updatedAt)
                throw new RecordConflictError(existing);
            const natural = typed;
            if (!existing && table === 'treatmentLogs' && natural.treatmentId && typeof natural.scheduledAt === 'number') {
                const matches = await target.where('[treatmentId+scheduledAt]').equals([natural.treatmentId, natural.scheduledAt]).toArray();
                existing = matches.sort((a, b) => b.updatedAt - a.updatedAt)[0];
                if (existing && matches.length > 1)
                    await Promise.all(matches.slice(1).map((row) => target.put({ ...row, duplicateOf: existing.id, updatedAt: Date.now() })));
            }
            if (!existing && table === 'routineLogs' && natural.routineId && typeof natural.scheduledAt === 'number') {
                const matches = await target.where('[routineId+scheduledAt]').equals([natural.routineId, natural.scheduledAt]).toArray();
                existing = matches.sort((a, b) => b.updatedAt - a.updatedAt)[0];
                if (existing && matches.length > 1)
                    await Promise.all(matches.slice(1).map((row) => target.put({ ...row, duplicateOf: existing.id, updatedAt: Date.now() })));
            }
            const record = {
                ...baseRecord(value),
                ...(existing ? { id: existing.id, createdAt: existing.createdAt } : {}),
                ...(typed.id ? { id: typed.id } : {}),
                updatedAt: Math.max(Date.now(), (existing?.updatedAt ?? 0) + 1),
            };
            // Attribution belongs to the original observation, not whoever edits or reopens it.
            if (existing && !existing.deletedAt) {
                if (existing.actorLabel)
                    record.actorLabel = existing.actorLabel;
                else
                    delete record.actorLabel;
            }
            else if (!record.actorLabel && 'petId' in record) {
                const actor = [...this.actorLabelResolvers].at(-1)?.(record.petId);
                if (actor)
                    record.actorLabel = actor.slice(0, 20);
            }
            await target.put(record);
            return record;
        });
    }
    async softDelete(table, id, expectedRevision) {
        await db.transaction('rw', [db[table], db.trash], async () => {
            const current = await this.get(table, id);
            if (!current || current.deletedAt)
                return;
            if (expectedRevision != null && current.updatedAt !== expectedRevision)
                throw new RecordConflictError(current);
            const now = Math.max(Date.now(), current.updatedAt + 1);
            const deletedAt = Date.now();
            await db.trash.add({ ...baseRecord({ table, snapshot: current, deletedAt }), table, snapshot: current, deletedAt, deletedRevision: now });
            await db[table].put({ ...current, deletedAt, updatedAt: now });
        });
    }
    async restoreTrash(id, replaceCurrentRevision) {
        const item = await db.trash.get(id);
        if (!item)
            return;
        const snapshot = item.snapshot;
        await db.transaction('rw', [db.trash, db[item.table]], async () => {
            const target = db[item.table];
            const current = await target.get(snapshot.id);
            const ownDeletion = current?.deletedAt === item.deletedAt && current.updatedAt === (item.deletedRevision ?? item.deletedAt);
            if (current && !ownDeletion && current.updatedAt !== replaceCurrentRevision)
                throw new RecordConflictError(current);
            const now = Math.max(Date.now(), (current?.updatedAt ?? 0) + 1);
            if (current && !ownDeletion)
                await db.trash.add({ ...baseRecord({ table: item.table, snapshot: current, deletedAt: now }), table: item.table, snapshot: current, deletedAt: now });
            await target.put({ ...snapshot, deletedAt: null, updatedAt: now });
            await db.trash.delete(id);
        });
    }
    async purgeTrash(olderThan = Date.now() - 7 * 86_400_000) {
        const stale = await db.trash.where('deletedAt').belowOrEqual(olderThan).toArray();
        await db.trash.bulkDelete(stale.map((row) => row.id));
        return stale.length;
    }
    async getSetting(key, fallback) {
        const row = await db.settings.where('key').equals(key).first();
        return row ? row.value : fallback;
    }
    async setSetting(key, value) {
        const existing = await db.settings.where('key').equals(key).first();
        const now = Date.now();
        await db.settings.put({ id: existing?.id ?? `setting-${key}`, key, value, createdAt: existing?.createdAt ?? now, updatedAt: now });
    }
}
export const repository = new LocalRepository();
